/**
 * Coups de pouce — Attendu 1125 « Calculer une moyenne pour un très petit
 * nombre de valeurs » (automatisme 4e).
 *
 * calcul_* (saisie, QCM) : entrée PARTAGÉE — additionner, compter, diviser
 * par le nombre de valeurs, contrôler par l'encadrement. L'exemple (revue
 * 2026-09-07) : 25, 40 et 31 min en barres, total 96, 96 ÷ 3 = 32, la ligne
 * de la moyenne nivelle les barres ; contrôle par l'encadrement.
 * SVG piloté par les flèches de la sheet (data-etapes / data-caption).
 *
 * rappel_tableau_vf : pur rappel, pas d'entrée (doctrine 2026-07-31).
 */

// Barres verticales à gauche (x 40 à 170), textes en colonne à droite (x ≥ 180).
const XB = [58, 103, 148]; const Y0 = 200; const K = 2.6;
const barre = (i, v, couleur) => `<rect x="${XB[i] - 15}" y="${(Y0 - v * K).toFixed(1)}" width="30" height="${(v * K).toFixed(1)}" fill="${couleur}" rx="3"/>`
    + `<text x="${XB[i]}" y="${(Y0 - v * K - 5).toFixed(1)}" font-size="11.5" font-weight="700" fill="#1e293b" text-anchor="middle">${v}</text>`;
const YM = (Y0 - 32 * K).toFixed(1);

const SVG_CALCUL = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Calculer la moyenne de trois durées, 25, 40 et 31 minutes, en 3 étapes à faire défiler avec les flèches. Étape 1 : les trois valeurs en barres. Étape 2 : le total 25 plus 40 plus 31 égale 96, on compte 3 valeurs, 96 divisé par 3 égale 32 ; une ligne à 32 nivelle les barres. Étape 3 : contrôle, 32 est entre 25 et 40, car la moyenne est toujours comprise entre la plus petite et la plus grande valeur">
  <style>
    @keyframes cp1125-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1125-pop { opacity: 0; animation: cp1125-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Trois durées : 25 min, 40 min, 31 min</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Total, puis division par le nombre de valeurs</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">On contrôle, et on évite deux pièges</text>

  <!-- ── Les trois barres (étape 1) ──────────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <line x1="36" y1="${Y0}" x2="172" y2="${Y0}" stroke="#1e293b" stroke-width="1.4"/>
    ${barre(0, 25, '#93C5FD')}${barre(1, 40, '#93C5FD')}${barre(2, 31, '#93C5FD')}
    <text x="103" y="220" font-size="10.5" font-weight="600" fill="#475569" text-anchor="middle">durées, en minutes</text>
  </g>

  <!-- ── Total et division (étape 2) ─────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1125-pop">
      <line x1="36" y1="${YM}" x2="172" y2="${YM}" stroke="#1D9E75" stroke-width="2.5" stroke-dasharray="6 4"/>
      <text x="32" y="${(Number(YM) + 4).toFixed(1)}" font-size="12" font-weight="800" fill="#1D9E75" text-anchor="end">32</text>
      <text x="184" y="52" font-size="11.5" font-weight="700" fill="#1D9E75">Total :</text>
      <text x="184" y="68" font-size="11.5" font-weight="700" fill="#1D9E75">25 + 40 + 31 = 96</text>
      <text x="184" y="90" font-size="11.5" font-weight="700" fill="#1D9E75">3 valeurs, donc :</text>
      <text x="184" y="106" font-size="11.5" font-weight="700" fill="#1D9E75">96 ÷ 3 = 32 min ✓</text>
      <text x="184" y="122" font-size="10" font-weight="600" fill="#1D9E75">(les barres nivelées valent 32)</text>
    </g>
  </g>

  <!-- ── Contrôle et pièges (étape 3) ────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1125-pop">
      <text x="184" y="146" font-size="11.5" font-weight="700" fill="#B45309">25 &lt; 32 &lt; 40 ✓</text>
      <text x="184" y="162" font-size="10" font-weight="600" fill="#B45309">la moyenne est entre la plus</text>
      <text x="184" y="174" font-size="10" font-weight="600" fill="#B45309">petite et la plus grande valeur</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Total ÷ nombre de valeurs, puis on contrôle.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_CALCUL = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**Additionne toutes les valeurs** pour obtenir le total.',
                '**Compte les valeurs**, puis divise le total par ce nombre. Trois valeurs : on divise par 3, pas par 2.',
                "**Vérifie** : la moyenne est toujours comprise entre la plus petite et la plus grande valeur. Elle n'est pas forcément l'une des valeurs.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_CALCUL },
    ],
};

// ─── Retrouver le total : 4 notes de moyenne 13 → 13 × 4 = 52 ──────────────
const XT = [50, 92, 134, 176]; const KT = 5;
const caseNote = (i, h, fill, stroke) => `<rect x="${XT[i] - 16}" y="${(Y0 - h * KT).toFixed(1)}" width="32" height="${(h * KT).toFixed(1)}" fill="${fill}" stroke="${stroke}" stroke-width="1.5" rx="3"/>`;

const SVG_TOTAL = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Retrouver le total de 4 notes dont la moyenne est 13, en 3 étapes à faire défiler avec les flèches. Étape 1 : quatre cases de même hauteur 13, la moyenne est le total partagé également. Étape 2 : total égale moyenne fois nombre de valeurs, 13 fois 4 égale 52. Étape 3 : contrôle, 52 est plus grand que 13 ; piège : 13 plus 4 égale 17 est faux">
  <style>
    @keyframes cp1125-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1125-pop { opacity: 0; animation: cp1125-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">4 notes, de moyenne 13 : quel total ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Le partage à l'envers : moyenne × nombre</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">On contrôle l'ordre de grandeur</text>

  <!-- ── Quatre cases de hauteur 13 (étape 1) ────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <line x1="28" y1="${Y0}" x2="200" y2="${Y0}" stroke="#1e293b" stroke-width="1.4"/>
    ${[0, 1, 2, 3].map((i) => caseNote(i, 13, '#DBEAFE', '#3B82F6')).join('')}
    ${[0, 1, 2, 3].map((i) => `<text x="${XT[i]}" y="${(Y0 - 13 * KT + 40).toFixed(1)}" font-size="12" font-weight="700" fill="#1E3A8A" text-anchor="middle">13</text>`).join('')}
    <text x="113" y="220" font-size="10.5" font-weight="600" fill="#475569" text-anchor="middle">la moyenne : chaque note « compte » 13</text>
  </g>

  <!-- ── Le calcul (étape 2) ─────────────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1125-pop">
      <path d="M 34 ${(Y0 - 13 * KT - 14).toFixed(1)} L 192 ${(Y0 - 13 * KT - 14).toFixed(1)}" stroke="#1D9E75" stroke-width="2"/>
      <text x="113" y="${(Y0 - 13 * KT - 20).toFixed(1)}" font-size="11" font-weight="700" fill="#1D9E75" text-anchor="middle">4 fois 13</text>
      <text x="212" y="80" font-size="11.5" font-weight="700" fill="#1D9E75">Total =</text>
      <text x="212" y="96" font-size="11.5" font-weight="700" fill="#1D9E75">moyenne × nombre</text>
      <text x="212" y="118" font-size="12.5" font-weight="800" fill="#1D9E75">13 × 4 = 52 ✓</text>
    </g>
  </g>

  <!-- ── Contrôle et piège (étape 3) ─────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1125-pop">
      <text x="212" y="152" font-size="11.5" font-weight="700" fill="#B45309">52 &gt; 13 ✓</text>
      <text x="212" y="166" font-size="10" font-weight="600" fill="#B45309">le total dépasse la moyenne</text>
      <text x="212" y="190" font-size="11.5" font-weight="700" fill="#B45309">13 + 4 = 17 ✗</text>
      <text x="212" y="204" font-size="10" font-weight="600" fill="#B45309">on multiplie, pas d'addition</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Total = moyenne × nombre de valeurs.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_TOTAL = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**La moyenne est le total partagé également** entre les valeurs : chaque valeur « compte » pour la moyenne.',
                '**Pour retrouver le total, on refait le partage à l\'envers** : total = moyenne × nombre de valeurs.',
                "**Vérifie l'ordre de grandeur** : le total est forcément plus grand que la moyenne (sauf s'il n'y a qu'une valeur).",
            ],
        },
        { titre: 'Un exemple', svg: SVG_TOTAL },
    ],
};

// ─── La moyenne, vrai ou faux ? : série 8 ; 15 ; 10 km → total 33, moyenne 11 ─
const cellule = (x, y, w, txt, fill, couleur, gras) => `<rect x="${x}" y="${y}" width="${w}" height="22" fill="${fill}" stroke="#94A3B8" stroke-width="1"/>`
    + `<text x="${x + w / 2}" y="${y + 15}" font-size="11.5" font-weight="${gras ? 700 : 600}" fill="${couleur}" text-anchor="middle">${txt}</text>`;
const LIGNE_VF = (y, txt, verdict, couleur) => `<text x="8" y="${y}" font-size="11" font-weight="700" fill="${couleur}">${verdict}</text><text x="34" y="${y}" font-size="11" font-weight="600" fill="#1e293b">${txt}</text>`;

const SVG_SERIE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Vrai ou faux sur la série 8, 15 et 10 kilomètres, en 3 étapes à faire défiler avec les flèches. Étape 1 : on calcule d'abord, total 33, moyenne 33 divisé par 3 égale 11. Étape 2 : la moyenne est 11, vrai ; on divise le total par 2, faux, il y a 3 valeurs. Étape 3 : la moyenne est l'une des valeurs, faux, 11 n'est pas dans la série ; la moyenne est comprise entre 8 et 10, faux, 11 dépasse 10">
  <style>
    @keyframes cp1125-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1125-pop { opacity: 0; animation: cp1125-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">D'abord le total et la moyenne</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">On juge avec ces deux nombres</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Où se place la moyenne ?</text>

  <!-- ── La série et le calcul (étape 1) ─────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${cellule(8, 36, 110, 'Distances (km)', '#1E3A8A', '#ffffff', true)}${cellule(118, 36, 40, '8', '#ffffff', '#1e293b')}${cellule(158, 36, 40, '15', '#ffffff', '#1e293b')}${cellule(198, 36, 40, '10', '#ffffff', '#1e293b')}
    <text x="8" y="80" font-size="11.5" font-weight="700" fill="#1E3A8A">Total : 8 + 15 + 10 = 33</text>
    <text x="8" y="96" font-size="11.5" font-weight="700" fill="#1E3A8A">Moyenne : 33 ÷ 3 = 11 km</text>
  </g>

  <!-- ── Deux affirmations sur le calcul (étape 2) ───────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1125-pop">
      ${LIGNE_VF(126, 'La moyenne de cette série est 11 km.', 'V', '#1D9E75')}
      ${LIGNE_VF(146, 'Pour trouver la moyenne, on divise le total par 2.', 'F', '#B45309')}
      <text x="34" y="160" font-size="10" font-weight="600" fill="#B45309">il y a 3 valeurs : on divise par 3</text>
    </g>
  </g>

  <!-- ── Deux affirmations de position (étape 3) ─────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1125-pop">
      ${LIGNE_VF(186, "La moyenne est l'une des valeurs de la série.", 'F', '#B45309')}
      <text x="34" y="200" font-size="10" font-weight="600" fill="#B45309">11 n'est ni 8, ni 15, ni 10</text>
      ${LIGNE_VF(220, 'La moyenne est comprise entre 8 km et 10 km.', 'F', '#B45309')}
      <text x="34" y="234" font-size="10" font-weight="600" fill="#B45309">11 dépasse 10 (mais 11 est bien entre 8 et 15)</text>
      <text x="180" y="248" font-size="11.5" font-weight="800" fill="#1E3A8A" text-anchor="middle">Calcule d'abord, juge ensuite.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_SERIE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**Commence par calculer** le total des valeurs, puis la moyenne = total ÷ nombre de valeurs. Avec ces deux nombres, tu peux juger chaque affirmation.',
                "**Le nombre de valeurs** est celui par lequel on divise le total, et celui par lequel on multiplie la moyenne pour retrouver le total. Ce n'est pas toujours 2.",
                "**La moyenne n'est pas forcément l'une des valeurs**, mais elle est toujours entre la plus petite et la plus grande.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_SERIE },
    ],
};

const CP_1125 = {
    calcul_saisie: AIDE_CALCUL,
    calcul_qcm_unique: AIDE_CALCUL,
    total_saisie: AIDE_TOTAL,
    serie_tableau_vf: AIDE_SERIE,
};

export default CP_1125;
